/**
 * Mixin para manejo optimizado de gráficos con grandes volúmenes de datos
 * Incluye procesamiento asíncrono, indicadores de carga y prevención de bloqueos
 */

import { AsyncDataProcessor, PerformanceUtils, CacheManager } from '@/utils/asyncDataProcessor';

/**
 * Helper para parsear valores monetarios en formato chileno
 * Convierte valores como "1.500.000" o "1,500,000.50" a número
 * @param {string|number} value - Valor a parsear
 * @returns {number} - Valor numérico
 */
function parseMonetaryValue(value) {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  
  // Convertir a string y limpiar espacios
  let stringValue = String(value).trim();
  
  // Remover símbolo de moneda si existe
  stringValue = stringValue.replace(/[$]/g, '');
  
  // Si el valor contiene puntos y comas, asumir formato chileno (1.500.000,50)
  if (stringValue.includes('.') && stringValue.includes(',')) {
    // Formato chileno: punto para miles, coma para decimales
    stringValue = stringValue.replace(/\./g, '').replace(',', '.');
  } else if (stringValue.includes('.') && !stringValue.includes(',')) {
    // Verificar si es formato chileno sin decimales (1.500.000) o formato US (1500.50)
    const parts = stringValue.split('.');
    if (parts.length === 2 && parts[1].length > 2) {
      // Probablemente formato chileno (1.500.000)
      stringValue = stringValue.replace(/\./g, '');
    }
    // Si tiene 2 decimales o menos, asumir formato US
  } else if (stringValue.includes(',') && !stringValue.includes('.')) {
    // Solo comas, puede ser formato US (1,500) o chileno con decimales (1500,50)
    const parts = stringValue.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      // Formato chileno con decimales (1500,50)
      stringValue = stringValue.replace(',', '.');
    } else {
      // Formato US con separador de miles (1,500)
      stringValue = stringValue.replace(/,/g, '');
    }
  }
  
  const parsed = parseFloat(stringValue);
  return isNaN(parsed) ? 0 : parsed;
}

export default {
  beforeDestroy() {
    // Limpiar timeouts pendientes
    if (this.chartUpdateTimeout) {
      clearTimeout(this.chartUpdateTimeout);
    }
  },
  
  data() {
    return {
      // Estados de carga por gráfico
      chartLoadingStates: {
        status: false,
        method: false,
        branch: false,
        trend: false,
        seller: false,
        category: false,
        planFamily: false,
        amount: false,
        modality: false,
        cashier: false,
        discountCategory: false,
        discountTrend: false
      },
      
      // Cache para optimizar recálculos
      chartCache: new CacheManager(50, 600000), // 10 minutos de TTL
      
      // Control de procesamiento
      isProcessingCharts: false,
      chartProcessingProgress: 0,
      lastChartUpdate: null,
      chartUpdateTimeout: null
    };
  },

  computed: {
    isAnyChartLoading() {
      return Object.values(this.chartLoadingStates).some(loading => loading);
    },
    
    chartLoadingPercentage() {
      const total = Object.keys(this.chartLoadingStates).length;
      const loaded = Object.values(this.chartLoadingStates).filter(loading => !loading).length;
      return Math.round((loaded / total) * 100);
    }
  },

  methods: {
    /**
     * Método de compatibilidad para mantener llamadas existentes
     */
    updateChartData() {
      console.log('📊 updateChartData llamado - delegando a updateChartDataAsync');
      return this.updateChartDataAsync();
    },
    
    /**
     * Actualiza los datos de los gráficos de forma asíncrona y optimizada
     */
    async updateChartDataAsync() {
      // Evitar actualizaciones múltiples concurrentes
      if (this.isProcessingCharts) {
        console.log('⚠️ Ya hay un procesamiento de gráficos en curso');
        return;
      }

      // Verificar si necesita actualización (debounce manual)
      const now = Date.now();
      if (this.lastChartUpdate && now - this.lastChartUpdate < 200) {
        console.log('⏳ Actualización de gráficos muy reciente, esperando...');
        // Programar una actualización después del tiempo de espera
        clearTimeout(this.chartUpdateTimeout);
        this.chartUpdateTimeout = setTimeout(() => {
          this.updateChartDataAsync();
        }, 300);
        return;
      }

      this.isProcessingCharts = true;
      this.chartProcessingProgress = 0;
      this.lastChartUpdate = now;

      const dataSource = this.allFilteredRecords && this.allFilteredRecords.length > 0 
        ? this.allFilteredRecords 
        : this.records;

      console.log('📊 Fuente de datos para gráficos:', {
        allFilteredRecords: this.allFilteredRecords ? this.allFilteredRecords.length : 'undefined',
        records: this.records ? this.records.length : 'undefined',
        dataSourceUsed: this.allFilteredRecords && this.allFilteredRecords.length > 0 ? 'allFilteredRecords' : 'records',
        dataSourceLength: dataSource ? dataSource.length : 0
      });

      if ((!dataSource || dataSource.length === 0) && !this.useServerMetrics) {
        console.log('📊 No hay datos para gráficos - reseteando');
        this.resetChartData();
        this.isProcessingCharts = false;
        
        // Si no hay datos pero deberíamos tenerlos, programar una actualización
        if ((this.allFilteredRecords === undefined || this.records === undefined) && !this.chartUpdateTimeout) {
          console.log('⏳ Datos aún no disponibles, programando actualización en 500ms...');
          this.chartUpdateTimeout = setTimeout(() => {
            this.updateChartDataAsync();
          }, 500);
        }
        return;
      }
      // Si tenemos métricas del servidor, ya no procesamos localmente
      if (this.useServerMetrics && this.serverCharts) {
        console.log('📊 Usando métricas del servidor para gráficos - salto procesamiento local');
        // Renderizar inmediatamente sin recalcular
        this.$nextTick(() => {
          if (this.renderCharts && typeof this.renderCharts === 'function') {
            this.renderCharts();
          }
        });
        this.isProcessingCharts = false;
        return;
      }

      console.log(`📊 Iniciando procesamiento asíncrono de gráficos con ${dataSource.length} registros`);

      try {
        // Procesar todos los gráficos en paralelo
        await Promise.all([
          this.updateStatusChartAsync(dataSource),
          this.updateMethodChartAsync(dataSource),
          this.updateBranchChartAsync(dataSource),
          this.updateTrendChartAsync(dataSource),
          this.updateSellerChartAsync(dataSource),
          this.updateCategoryChartAsync(dataSource),
          this.updatePlanFamilyChartAsync(dataSource),
          this.updateAmountChartAsync(dataSource),
          this.updateModalityChartAsync(dataSource),
          this.updateCashierChartAsync(dataSource),
          this.updateDiscountChartsAsync(dataSource)
        ]);

        console.log('✅ Todos los gráficos actualizados exitosamente');
        
        // Renderizar los gráficos después de actualizar los datos
        this.$nextTick(() => {
          if (this.renderCharts && typeof this.renderCharts === 'function') {
            this.renderCharts();
          }
        });

      } catch (error) {
        console.error('❌ Error al actualizar gráficos:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error en gráficos',
          text: 'Hubo un problema al generar los gráficos. Por favor, intente nuevamente.',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.isProcessingCharts = false;
      }
    },

    /**
     * Actualiza el gráfico de estado de forma asíncrona
     */
    async updateStatusChartAsync(dataSource) {
      this.chartLoadingStates.status = true;
      
      try {
        const cacheKey = { type: 'status', count: dataSource.length };
        const cached = this.chartCache.get(cacheKey);
        
        if (cached) {
          this.chartData.salesByStatus = cached;
          return;
        }

        const aggregation = await AsyncDataProcessor.groupAndCount(
          dataSource,
          (sale) => this.getStatusText(sale.estadoVenta),
          (sale) => parseMonetaryValue(sale.ventaTotal) || 0
        );

        const chartData = Object.entries(aggregation)
          .map(([status, data]) => ({
            label: status,
            value: data.count,
            amount: data.amount
          }))
          .sort((a, b) => b.value - a.value);

        this.chartData.salesByStatus = chartData;
        this.chartCache.set(cacheKey, chartData);
        
      } finally {
        this.chartLoadingStates.status = false;
      }
    },

    /**
     * Actualiza el gráfico de métodos de pago de forma asíncrona
     */
    async updateMethodChartAsync(dataSource) {
      this.chartLoadingStates.method = true;
      
      try {
        const aggregation = await AsyncDataProcessor.groupAndCount(
          dataSource,
          (sale) => sale.metodoPago || 'Sin método',
          (sale) => parseMonetaryValue(sale.ventaTotal) || 0
        );

        this.chartData.salesByMethod = Object.entries(aggregation)
          .map(([method, data]) => ({
            label: method,
            value: data.count,
            amount: data.amount
          }))
          .sort((a, b) => b.value - a.value);
        
      } finally {
        this.chartLoadingStates.method = false;
      }
    },

    /**
     * Actualiza el gráfico de sucursales de forma asíncrona
     */
    async updateBranchChartAsync(dataSource) {
      this.chartLoadingStates.branch = true;
      
      try {
        const aggregation = await AsyncDataProcessor.groupAndCount(
          dataSource,
          (sale) => sale.nombreSucursal || 'Sin sucursal',
          (sale) => parseMonetaryValue(sale.ventaTotal) || 0
        );

        this.chartData.salesByBranch = Object.entries(aggregation)
          .map(([branch, data]) => ({
            label: branch,
            value: data.count,
            amount: data.amount
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10); // Top 10 sucursales
        
      } finally {
        this.chartLoadingStates.branch = false;
      }
    },

    /**
     * Actualiza el gráfico de tendencia diaria de forma asíncrona
     */
    async updateTrendChartAsync(dataSource) {
      this.chartLoadingStates.trend = true;
      
      try {
        const aggregation = await AsyncDataProcessor.groupAndCount(
          dataSource,
          (sale) => {
            if (!sale.fechaHora) return null;
            try {
              const date = this.parseChileanDate(sale.fechaHora);
              if (date && !isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              }
            } catch (error) {
              return null;
            }
          },
          (sale) => parseMonetaryValue(sale.ventaTotal) || 0
        );

        this.chartData.dailyTrend = Object.entries(aggregation)
          .filter(([date]) => date !== 'null')
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, data]) => ({
            label: date,
            value: data.count,
            amount: data.amount
          }));
        
      } finally {
        this.chartLoadingStates.trend = false;
      }
    },

    /**
     * Actualiza el gráfico de vendedores de forma asíncrona
     */
    async updateSellerChartAsync(dataSource) {
      this.chartLoadingStates.seller = true;
      
      try {
        const aggregation = await AsyncDataProcessor.groupAndCount(
          dataSource,
          (sale) => sale.nombreVendedor || 'Sin vendedor',
          (sale) => parseMonetaryValue(sale.ventaTotal) || 0
        );

        this.chartData.salesBySeller = Object.entries(aggregation)
          .filter(([seller]) => seller && seller.trim())
          .sort(([,a], [,b]) => b.count - a.count)
          .slice(0, 10) // Top 10 vendedores
          .map(([seller, data]) => ({
            label: seller,
            value: data.count,
            amount: data.amount
          }));
        
      } finally {
        this.chartLoadingStates.seller = false;
      }
    },

    /**
     * Actualiza el gráfico de categorías de forma asíncrona
     */
    async updateCategoryChartAsync(dataSource) {
      this.chartLoadingStates.category = true;
      
      try {
        const categoryData = {};
        
        await AsyncDataProcessor.processInChunks(
          dataSource,
          (chunk) => {
            chunk.forEach(sale => {
              if (!sale.infoVenta) return;
              
              // Nota: Los planes se procesarán a través de detalleVenta con categoriaProducto = 'PLANES'
              // No es necesario procesarlos por separado
              
              // Procesar productos
              if (Array.isArray(sale.infoVenta.detalleVenta)) {
                sale.infoVenta.detalleVenta.forEach(item => {
                  if (item.categoriaProducto && 
                      item.categoriaProducto !== 'DESCUENTO' && 
                      !item.categoriaProducto.includes('DESCUENTO') &&
                      item.categoriaProducto !== 'PLANES DESCUENTOS' &&
                      item.categoriaProducto !== 'DESCUENTOS GENERALES') {
                    
                    const category = item.categoriaProducto;
                    let amount = 0;
                    
                    // Para RECARGOS GENERALES, usar el precio directamente ya que no aplican descuentos
                    if (category === 'RECARGOS GENERALES') {
                      amount = parseMonetaryValue(item.precio) || 0;
                    } else {
                      // Para otras categorías, aplicar la fórmula con descuento
                      const precioUnitarioBruto = parseMonetaryValue(item.precioUnitarioBruto) || 0;
                      const descuento = parseFloat(item.porcentajeDescuento) || 0;
                      amount = precioUnitarioBruto * (1 - descuento / 100);
                    }
                    
                    if (!categoryData[category]) {
                      categoryData[category] = { count: 0, amount: 0 };
                    }
                    categoryData[category].count += 1;
                    categoryData[category].amount += amount;
                  }
                });
              }
            });
          },
          { chunkSize: 200 }
        );

        this.chartData.salesByCategory = Object.entries(categoryData)
          .sort(([,a], [,b]) => b.amount - a.amount)
          .slice(0, 10)
          .map(([category, data]) => ({
            label: category,
            value: data.count,
            amount: data.amount
          }));
        
      } finally {
        this.chartLoadingStates.category = false;
      }
    },

    /**
     * Actualiza el gráfico de Ventas por Plan (antes familia de plan)
     * Filtra solo items con categoriaProducto == "PLANES"
     */
    async updatePlanFamilyChartAsync(dataSource) {
      this.chartLoadingStates.planFamily = true;
      
      try {
        const planData = {};
        
        await AsyncDataProcessor.processInChunks(
          dataSource,
          (chunk) => {
            chunk.forEach(sale => {
              if (sale.infoVenta?.detalleVenta) {
                sale.infoVenta.detalleVenta.forEach(item => {
                  // Filtrar solo items de categoría PLANES
                  if (item.categoriaProducto === 'PLANES') {
                    const planName = item.detalle || 'Sin nombre';
                    const precio = parseMonetaryValue(item.precio) || 0;
                    const descuento = parseFloat(item.porcentajeDescuento) || 0;
                    const amount = precio * (1 - descuento / 100);
                    
                    if (!planData[planName]) {
                      planData[planName] = { count: 0, amount: 0 };
                    }
                    planData[planName].count += 1;
                    planData[planName].amount += amount;
                  }
                });
              }
            });
          },
          { chunkSize: 200 }
        );

        this.chartData.salesByPlanFamily = Object.entries(planData)
          .sort(([,a], [,b]) => b.amount - a.amount)
          .slice(0, 10)
          .map(([plan, data]) => ({
            label: plan,
            value: data.count,
            amount: data.amount
          }));
        
      } finally {
        this.chartLoadingStates.planFamily = false;
      }
    },

    /**
     * Actualiza el gráfico de distribución de montos de forma asíncrona
     */
    async updateAmountChartAsync(dataSource) {
      this.chartLoadingStates.amount = true;
      
      try {
        const ranges = {
          '< $10.000': { min: 0, max: 10000, count: 0, amount: 0 },
          '$10.000 - $50.000': { min: 10000, max: 50000, count: 0, amount: 0 },
          '$50.000 - $100.000': { min: 50000, max: 100000, count: 0, amount: 0 },
          '$100.000 - $500.000': { min: 100000, max: 500000, count: 0, amount: 0 },
          '> $500.000': { min: 500000, max: Infinity, count: 0, amount: 0 }
        };

        await AsyncDataProcessor.processInChunks(
          dataSource,
          (chunk) => {
            chunk.forEach(sale => {
              const amount = parseMonetaryValue(sale.ventaTotal) || 0;
              
              for (const [label, range] of Object.entries(ranges)) {
                if (amount >= range.min && amount < range.max) {
                  range.count += 1;
                  range.amount += amount;
                  break;
                }
              }
            });
          },
          { chunkSize: 500 }
        );

        this.chartData.amountDistribution = Object.entries(ranges)
          .map(([label, data]) => ({
            label,
            value: data.count,
            amount: data.amount
          }));
        
      } finally {
        this.chartLoadingStates.amount = false;
      }
    },

    /**
     * Actualiza el gráfico de Ventas por Tipo de Plan (antes modalidades)
     */
    async updateModalityChartAsync(dataSource) {
      this.chartLoadingStates.modality = true;
      
      try {
        const tipoPlanData = {};
        
        await AsyncDataProcessor.processInChunks(
          dataSource,
          (chunk) => {
            chunk.forEach(sale => {
              if (sale.infoVenta?.plan && sale.infoVenta.plan.length > 0) {
                // Usar solo el primer plan (plan[0])
                const plan = sale.infoVenta.plan[0];
                const tipoPlan = plan.tipoDePlan || 'Sin tipo';
                const amount = parseMonetaryValue(plan.monto) || 0;
                
                if (!tipoPlanData[tipoPlan]) {
                  tipoPlanData[tipoPlan] = { count: 0, amount: 0 };
                }
                tipoPlanData[tipoPlan].count += 1;
                tipoPlanData[tipoPlan].amount += amount;
              }
            });
          },
          { chunkSize: 200 }
        );

        this.chartData.salesByModality = Object.entries(tipoPlanData)
          .sort(([,a], [,b]) => b.count - a.count)
          .slice(0, 10)
          .map(([tipoPlan, data]) => ({
            label: tipoPlan,
            value: data.count,
            amount: data.amount
          }));
        
      } finally {
        this.chartLoadingStates.modality = false;
      }
    },

    /**
     * Actualiza el gráfico de cajas de forma asíncrona
     */
    async updateCashierChartAsync(dataSource) {
      this.chartLoadingStates.cashier = true;
      
      try {
        const aggregation = await AsyncDataProcessor.groupAndCount(
          dataSource,
          (sale) => sale.cajaPuntoDeVenta || 'Sin caja',
          (sale) => parseMonetaryValue(sale.ventaTotal) || 0
        );

        this.chartData.salesByCashier = Object.entries(aggregation)
          .sort(([,a], [,b]) => b.count - a.count)
          .slice(0, 10)
          .map(([cashier, data]) => ({
            label: cashier,
            value: data.count,
            amount: data.amount
          }));
        
      } finally {
        this.chartLoadingStates.cashier = false;
      }
    },

    /**
     * Actualiza los gráficos de descuentos de forma asíncrona
     */
    async updateDiscountChartsAsync(dataSource) {
      this.chartLoadingStates.discountCategory = true;
      this.chartLoadingStates.discountTrend = true;
      
      try {
        const discountCategoryData = {};
        const discountDailyData = {};
        
        await AsyncDataProcessor.processInChunks(
          dataSource,
          (chunk) => {
            chunk.forEach(sale => {
              if (sale.infoVenta?.detalleVenta) {
                // Analizar descuentos por categoría
                sale.infoVenta.detalleVenta.forEach(item => {
                  if (item.porcentajeDescuento && parseFloat(item.porcentajeDescuento) > 0) {
                    const category = item.categoriaProducto || 'Sin categoría';
                    const descuento = parseFloat(item.porcentajeDescuento) || 0;
                    const precioUnitarioBruto = parseMonetaryValue(item.precioUnitarioBruto) || 0;
                    const montoDescuento = precioUnitarioBruto * (descuento / 100);
                    
                    if (!discountCategoryData[category]) {
                      discountCategoryData[category] = { count: 0, amount: 0 };
                    }
                    discountCategoryData[category].count += 1;
                    discountCategoryData[category].amount += montoDescuento;
                  }
                });
                
                // Tendencia diaria de descuentos
                if (sale.fechaHora) {
                  try {
                    const date = this.parseChileanDate(sale.fechaHora);
                    if (date && !isNaN(date.getTime())) {
                      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                      
                      const totalDescuento = sale.infoVenta.detalleVenta.reduce((sum, item) => {
                        if (item.porcentajeDescuento && parseFloat(item.porcentajeDescuento) > 0) {
                          const descuento = parseFloat(item.porcentajeDescuento) || 0;
                          const precioUnitarioBruto = parseMonetaryValue(item.precioUnitarioBruto) || 0;
                          const montoDescuento = precioUnitarioBruto * (descuento / 100);
                          return sum + montoDescuento;
                        }
                        return sum;
                      }, 0);
                      
                      if (totalDescuento > 0) {
                        if (!discountDailyData[dateStr]) {
                          discountDailyData[dateStr] = { count: 0, amount: 0 };
                        }
                        discountDailyData[dateStr].count += 1;
                        discountDailyData[dateStr].amount += totalDescuento;
                      }
                    }
                  } catch (error) {
                    // Ignorar errores de fecha
                  }
                }
              }
            });
          },
          { chunkSize: 200 }
        );

        // Procesar datos de categorías de descuento
        this.chartData.discountsByCategory = Object.entries(discountCategoryData)
          .sort(([,a], [,b]) => b.amount - a.amount)
          .slice(0, 10)
          .map(([category, data]) => ({
            label: category,
            value: data.count,
            amount: data.amount
          }));
        
        // Procesar tendencia diaria de descuentos (valores negativos)
        this.chartData.discountsDailyTrend = Object.entries(discountDailyData)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, data]) => ({
            label: date,
            value: data.count,
            amount: -data.amount  // Negativo para mostrar descuentos
          }));
        
      } finally {
        this.chartLoadingStates.discountCategory = false;
        this.chartLoadingStates.discountTrend = false;
      }
    },

    /**
     * Parsea fecha en formato chileno DD-MM-YYYY HH:MM:SS
     */
    parseChileanDate(dateStr) {
      if (!dateStr) return null;
      
      try {
        // Formato esperado: "DD-MM-YYYY HH:MM:SS"
        const [datePart, timePart] = dateStr.split(' ');
        const [day, month, year] = datePart.split('-').map(Number);
        
        if (timePart) {
          const [hours, minutes, seconds] = timePart.split(':').map(Number);
          return new Date(year, month - 1, day, hours, minutes, seconds);
        } else {
          return new Date(year, month - 1, day);
        }
      } catch (error) {
        console.error('Error parseando fecha chilena:', dateStr, error);
        return null;
      }
    },
    
    /**
     * Resetea todos los datos de gráficos
     */
    resetChartData() {
      this.chartData = {
        salesByStatus: [],
        salesByMethod: [],
        salesByBranch: [],
        dailyTrend: [],
        salesBySeller: [],
        salesByCategory: [],
        salesByPlanFamily: [],
        amountDistribution: [],
        salesByModality: [],
        salesByCashier: [],
        discountsByCategory: [],
        discountsDailyTrend: []
      };
      
      // Resetear estados de carga
      Object.keys(this.chartLoadingStates).forEach(key => {
        this.chartLoadingStates[key] = false;
      });
    },

    /**
     * Muestra un spinner de carga en un canvas específico
     */
    showChartLoadingSpinner(canvasRef) {
      if (!this.$refs[canvasRef]) return;
      
      const canvas = this.$refs[canvasRef];
      const ctx = canvas.getContext('2d');
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      // Limpiar canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Dibujar spinner
      ctx.save();
      ctx.translate(centerX, centerY);
      
      // Círculo de carga
      ctx.beginPath();
      ctx.arc(0, 0, 20, 0, Math.PI * 2);
      ctx.strokeStyle = '#ddd';
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Arco animado
      const progress = (Date.now() / 1000) % 1;
      ctx.beginPath();
      ctx.arc(0, 0, 20, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * progress));
      ctx.strokeStyle = '#007bff';
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Texto
      ctx.fillStyle = '#666';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Cargando...', 0, 40);
      
      ctx.restore();
      
      // Continuar animación si sigue cargando
      if (this.chartLoadingStates[canvasRef.replace('Chart', '')]) {
        requestAnimationFrame(() => this.showChartLoadingSpinner(canvasRef));
      }
    }
  }
};